import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CellHookData } from 'jspdf-autotable';
import type {
  BandwidthActivityAllocationRow,
  BandwidthActivityTypeCountTimePoint,
  BandwidthAllocationMetrics,
  BandwidthCapacityDeductionCell,
  BandwidthGroupBreakdownRow,
  BandwidthHeatmapCountsCustomerExportRow,
  BandwidthTeamResourceAllocationRow,
  BandwidthUserCountTimePoint,
} from './bandwidthDashboardTypes';
import {
  buildCapacityDeductionLookups,
  getCapacityDeductionHoursForGroupUser,
  getEffectiveBandwidthCapacityHours,
  getGroupEffectiveCapacityHours,
} from './bandwidthCapacityDeductionTypes';
import { bandwidthExportDateFileSuffix } from './bandwidthExportFileNaming';
import {
  allocationExportPctPdf,
  computeActivityAllocationFooter,
  computeAllocationFooter,
} from './bandwidthAllocation';
import { bwExportDecimal, bwExportMinutes, bwExportPercentPdf, roundBwDecimal } from './bandwidthFormat';
import { exportHeatmapCellValue, type HeatmapValueUnit } from './bandwidthHeatmapMatrix';
import {
  prepareBandwidthCustomerSectionExport,
  prepareBandwidthGroupSectionExport,
  type BandwidthCustomerSectionExportRow,
} from './bandwidthKpiExportFilter';
import {
  getBandwidthCapacityHoursForWorkLocation,
  getBandwidthUtilizationPct,
} from './bandwidthUtilization';
const PDF_HEADER_BG: [number, number, number] = [68, 114, 196];
const PDF_HEADER_TEXT: [number, number, number] = [255, 255, 255];
const PDF_TITLE: [number, number, number] = [30, 41, 59];
const PDF_BODY_TEXT: [number, number, number] = [15, 23, 42];
const PDF_MUTED: [number, number, number] = [100, 116, 139];
const PDF_BORDER: [number, number, number] = [199, 210, 254];
const PDF_ZEBRA_A: [number, number, number] = [255, 255, 255];
const PDF_ZEBRA_B: [number, number, number] = [248, 250, 252];
/** Group / customer summary row */
const PDF_SECTION_BG: [number, number, number] = [238, 242, 255];
/** Grand total row */
const PDF_TOTAL_BG: [number, number, number] = [226, 232, 240];

function bwPdfFilename(base: string, dateFrom: string, dateTo: string): string {
  return `${base}_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}.pdf`;
}

export type BandwidthPdfExport = { blob: Blob; fileName: string };

/** jsPDF built-in fonts only support WinAnsi; replace common Unicode punctuation. */
function pdfAsciiSafeText(s: string): string {
  return s
    .replace(/\u2192/g, ' to ')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ');
}

function toYmdDisplay(isoOrDate: string): string {
  const s = (isoOrDate || '').trim();
  const d = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const cleaned = s.replace(/[^\d-]/g, '').slice(0, 10);
  return cleaned || 'n/a';
}

/** ASCII only — jsPDF standard fonts (Helvetica) are WinAnsi; Unicode arrows/dashes corrupt `doc.text()`. */
function periodSubtitle(dateFrom: string, dateTo: string): string {
  return `Duration: ${toYmdDisplay(dateFrom)} to ${toYmdDisplay(dateTo)}`;
}

/** Draw title + period; returns Y position to start autoTable. */
function drawBandwidthPdfTitleBlock(
  doc: jsPDF,
  title: string,
  dateFrom: string,
  dateTo: string,
  opts?: { unitNote?: string; titleY?: number }
): number {
  const margin = 14;
  const titleY = opts?.titleY ?? 12;
  doc.setTextColor(...PDF_TITLE);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(pdfAsciiSafeText(title), margin, titleY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF_MUTED);
  const periodY = titleY + 5.5;
  doc.text(periodSubtitle(dateFrom, dateTo), margin, periodY);
  let lastTextY = periodY;
  if (opts?.unitNote) {
    doc.setFontSize(8);
    lastTextY = periodY + 4;
    doc.text(pdfAsciiSafeText(opts.unitNote), margin, lastTextY);
  }
  doc.setTextColor(...PDF_BODY_TEXT);
  return lastTextY + 6;
}

function finalizeBandwidthPdfFooters(doc: jsPDF): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  const generated = new Date().toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_MUTED);
    doc.text(pdfAsciiSafeText(`Generated ${generated}`), 14, pageH - 8);
    doc.text(`Page ${i} of ${total}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.setDrawColor(...PDF_BORDER);
    doc.setLineWidth(0.25);
    doc.line(14, pageH - 11, pageW - 14, pageH - 11);
    doc.setTextColor(...PDF_BODY_TEXT);
  }
}

const defaultHeadStyles = {
  fillColor: PDF_HEADER_BG,
  textColor: PDF_HEADER_TEXT,
  fontStyle: 'bold' as const,
  fontSize: 8,
  cellPadding: 2,
};

const defaultTableStyles = {
  fontSize: 8,
  cellPadding: 2,
  textColor: PDF_BODY_TEXT,
  lineColor: PDF_BORDER,
  lineWidth: 0.12,
};

/** Avoid jspdf-autotable default striping; we set fills in `didParseCell`. */
const pdfTablePlainTheme = { theme: 'plain' as const };

export type BandwidthCustomerSectionPdfRow = BandwidthCustomerSectionExportRow;

function styleBodyCellGrouped(data: CellHookData, boldRows: Set<number>, lastBodyRowIndex: number): void {
  if (data.section !== 'body') return;
  const idx = data.row.index;
  const isTotal = idx === lastBodyRowIndex;
  const isSection = boldRows.has(idx) && !isTotal;
  if (isTotal) {
    data.cell.styles.fillColor = PDF_TOTAL_BG;
    data.cell.styles.fontStyle = 'bold';
    data.cell.styles.textColor = PDF_BODY_TEXT;
    return;
  }
  if (isSection) {
    data.cell.styles.fillColor = PDF_SECTION_BG;
    data.cell.styles.fontStyle = 'bold';
    return;
  }
  data.cell.styles.fillColor = idx % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
}

function styleBodyCellSimpleTotal(data: CellHookData, boldRows: Set<number>): void {
  if (data.section !== 'body') return;
  const idx = data.row.index;
  if (boldRows.has(idx)) {
    data.cell.styles.fillColor = PDF_TOTAL_BG;
    data.cell.styles.fontStyle = 'bold';
    return;
  }
  data.cell.styles.fillColor = idx % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
}

export function exportBandwidthGroupSectionPdf(
  groups: BandwidthGroupBreakdownRow[],
  dateFrom: string,
  dateTo: string,
  capacityDeductionByGroupUser: BandwidthCapacityDeductionCell[] = []
): BandwidthPdfExport {
  const exportGroups = prepareBandwidthGroupSectionExport(groups);
  const deductionLookups = buildCapacityDeductionLookups(capacityDeductionByGroupUser);
  const dateLabel = `${toYmdDisplay(dateFrom)} to ${toYmdDisplay(dateTo)}`;
  const head = [
    ['Teams', 'Date', 'User', 'Total activity count', 'Total time spent (hrs)', 'Avg hrs on activity', 'Utilization %'],
  ];
  const body: (string | number)[][] = [];
  const boldRows = new Set<number>();

  let sumCount = 0;
  let sumHrs = 0;
  let sumCapacityHours = 0;
  let rowIdx = 0;
  for (const g of exportGroups) {
    boldRows.add(rowIdx);
    const groupCap = getGroupEffectiveCapacityHours(g, dateFrom, dateTo, deductionLookups);
    const groupCapPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, g.workLocation);
    sumCapacityHours += groupCap;
    const gUtil = getBandwidthUtilizationPct(g.timeSpentHours, groupCap);
    body.push([
      g.groupName,
      dateLabel,
      '',
      g.totalActivityCount,
      bwExportDecimal(g.timeSpentHours),
      bwExportDecimal(g.avgHrsOnActivity),
      bwExportPercentPdf(gUtil),
    ]);
    rowIdx++;
    sumCount += g.totalActivityCount;
    sumHrs += g.timeSpentHours;
    for (const u of g.users) {
      const userDeductionHours = getCapacityDeductionHoursForGroupUser(
        deductionLookups,
        g.groupId,
        u.userId
      );
      const userCapHours = getEffectiveBandwidthCapacityHours(groupCapPerPerson, userDeductionHours);
      const uUtil = getBandwidthUtilizationPct(u.timeSpentHours, userCapHours);
      body.push([
        g.groupName,
        dateLabel,
        u.userName,
        u.totalActivityCount,
        bwExportDecimal(u.timeSpentHours),
        bwExportDecimal(u.avgHrsOnActivity),
        bwExportPercentPdf(uUtil),
      ]);
      rowIdx++;
    }
  }
  const grandAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const grandUtil = getBandwidthUtilizationPct(sumHrs, sumCapacityHours);
  boldRows.add(rowIdx);
  body.push([
    'Total',
    dateLabel,
    '',
    sumCount,
    bwExportDecimal(sumHrs),
    grandAvg,
    bwExportPercentPdf(grandUtil),
  ]);
  const lastBodyIdx = body.length - 1;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, 'Bandwidth Dashboard - Teams', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 46 },
      1: { cellWidth: 36 },
      2: { cellWidth: 38 },
      3: { halign: 'right', cellWidth: 32 },
      4: { halign: 'right', cellWidth: 34 },
      5: { halign: 'right', cellWidth: 30 },
      6: { halign: 'right', cellWidth: 26 },
    },
    didParseCell: (data) => styleBodyCellGrouped(data, boldRows, lastBodyIdx),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_dashboard_teams', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthCustomerSectionPdf(
  rows: BandwidthCustomerSectionPdfRow[],
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport {
  const exportRows = prepareBandwidthCustomerSectionExport(rows);
  const head = [['Customer', 'Customer type', 'Assigned user', 'Total activity count', 'Total time spent (hrs)', 'Avg hrs on activity']];
  const body: (string | number)[][] = [];
  const boldRows = new Set<number>();

  let sumCount = 0;
  let sumHrs = 0;
  let rowIdx = 0;
  for (const r of exportRows) {
    boldRows.add(rowIdx);
    body.push([
      r.customer,
      r.customerType ?? '',
      '',
      r.totalActivity,
      bwExportDecimal(r.timeHrs),
      bwExportDecimal(r.avgHrsOnActivity),
    ]);
    rowIdx++;
    sumCount += r.totalActivity;
    sumHrs += r.timeHrs;
    for (const u of r.users) {
      body.push([
        r.customer,
        r.customerType ?? '',
        u.userName,
        u.totalActivity,
        bwExportDecimal(u.timeHrs),
        bwExportDecimal(u.avgHrsOnActivity),
      ]);
      rowIdx++;
    }
  }
  const totalAvgHrs = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  boldRows.add(rowIdx);
  body.push(['Total', '', '', sumCount, bwExportDecimal(sumHrs), totalAvgHrs]);
  const lastBodyIdx = body.length - 1;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, 'Bandwidth Dashboard - Customer', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 56 },
      1: { cellWidth: 26 },
      2: { cellWidth: 44 },
      3: { halign: 'right', cellWidth: 34 },
      4: { halign: 'right', cellWidth: 38 },
      5: { halign: 'right', cellWidth: 34 },
    },
    didParseCell: (data) => styleBodyCellGrouped(data, boldRows, lastBodyIdx),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_dashboard_customer', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthHeatmapMatrixPdf(params: {
  documentTitle: string;
  filePrefix: string;
  rowLabelHeader: string;
  rowLabels: string[];
  columnLabels: string[];
  cells: number[][];
  dateFrom: string;
  dateTo: string;
  /** Shown under title, e.g. "Values are minutes." */
  unitNote?: string;
  /** When `min`, export whole minutes; when `percent`, export 2-decimal percent values. */
  cellValueUnit?: HeatmapValueUnit;
}): BandwidthPdfExport | null {
  const { documentTitle, filePrefix, rowLabelHeader, rowLabels, columnLabels, cells, unitNote, dateFrom, dateTo, cellValueUnit = 'min' } =
    params;
  if (rowLabels.length === 0 || columnLabels.length === 0) return null;

  const head = [[rowLabelHeader, ...columnLabels]];
  const body: (string | number)[][] = [];
  const colSums = columnLabels.map((_, ci) => {
    const sum = rowLabels.reduce((s, _, ri) => s + (cells[ri]?.[ci] ?? 0), 0);
    return exportHeatmapCellValue(sum, cellValueUnit);
  });

  for (let ri = 0; ri < rowLabels.length; ri++) {
    const row = cells[ri] ?? [];
    body.push([
      rowLabels[ri],
      ...columnLabels.map((_, ci) => {
        const v = row[ci] ?? 0;
        return exportHeatmapCellValue(v, cellValueUnit);
      }),
    ]);
  }
  const lastIdx = body.length;
  body.push(['Total', ...colSums]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, documentTitle, dateFrom, dateTo, { unitNote });
  const fs = columnLabels.length > 16 ? 5.5 : columnLabels.length > 10 ? 6.5 : 7.5;
  const pad = columnLabels.length > 14 ? 0.7 : 1.2;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: {
      ...defaultTableStyles,
      fontSize: fs,
      cellPadding: pad,
    },
    headStyles: { ...defaultHeadStyles, fontSize: fs, cellPadding: pad },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename(filePrefix, dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthHeatmapCountsByCustomerPdf(
  rows: BandwidthHeatmapCountsCustomerExportRow[],
  documentTitle: string,
  filePrefix: string,
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport {
  const head = [['Customer', 'Customer type', 'Total activity count', 'Time spent (minutes)', 'Time spent (hrs)', 'Avg hrs / activity', 'Utilization %']];
  const body: (string | number)[][] = [];
  let sumAct = 0;
  let sumMin = 0;
  let sumHrs = 0;
  for (const r of rows) {
    sumAct += r.totalActivity;
    sumMin += r.timeMinutes;
    sumHrs += r.timeHrs;
  }
  for (const r of rows) {
    body.push([
      r.customer,
      r.customerType ?? '',
      r.totalActivity,
      bwExportMinutes(r.timeMinutes),
      bwExportDecimal(r.timeHrs),
      bwExportDecimal(r.avgHrsOnActivity),
      bwExportDecimal(r.utilizationPct),
    ]);
  }
  const totalAvg = sumAct > 0 ? roundBwDecimal(sumHrs / sumAct) : '';
  const totalUtil = getBandwidthUtilizationPct(sumHrs, sumHrs);
  const lastIdx = body.length;
  body.push([
    'Total',
    '',
    sumAct,
    bwExportMinutes(sumMin),
    bwExportDecimal(sumHrs),
    totalAvg,
    bwExportDecimal(totalUtil),
  ]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, documentTitle, dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 46 },
      1: { cellWidth: 24 },
      2: { halign: 'right', cellWidth: 26 },
      3: { halign: 'right', cellWidth: 30 },
      4: { halign: 'right', cellWidth: 26 },
      5: { halign: 'right', cellWidth: 28 },
      6: { halign: 'right', cellWidth: 24 },
    },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename(filePrefix, dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthLabelCountPdf(
  rows: Array<{ label: string; count: number }>,
  documentTitle: string,
  filePrefix: string,
  rowHeader: string,
  valueHeader: string,
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport {
  const head = [[rowHeader, valueHeader]];
  const body: (string | number)[][] = [];
  let sum = 0;
  for (const r of rows) {
    body.push([r.label, r.count]);
    sum += r.count;
  }
  const lastIdx = body.length;
  body.push(['Total', sum]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, documentTitle, dateFrom, dateTo, { titleY: 14 });
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: { ...defaultTableStyles, fontSize: 9, cellPadding: 2.2 },
    headStyles: { ...defaultHeadStyles, fontSize: 9, cellPadding: 2.2 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 40 } },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename(filePrefix, dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthActivityTypeCountTimePdf(
  rows: BandwidthActivityTypeCountTimePoint[],
  documentTitle: string,
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport {
  const head = [['Activity type', 'Activity count', 'Time (hrs)', 'Avg hrs / activity']];
  const body: (string | number)[][] = [];
  let sumCount = 0;
  let sumHrs = 0;
  for (const r of rows) {
    const avg = r.totalActivityCount > 0 ? r.timeSpentHours / r.totalActivityCount : null;
    body.push([
      r.activityTypeLabel,
      r.totalActivityCount,
      bwExportDecimal(r.timeSpentHours),
      bwExportDecimal(avg),
    ]);
    sumCount += r.totalActivityCount;
    sumHrs += r.timeSpentHours;
  }
  const totalAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const lastIdx = body.length;
  body.push(['Total', sumCount, bwExportDecimal(sumHrs), totalAvg]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, documentTitle, dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 75 },
      1: { halign: 'right', cellWidth: 32 },
      2: { halign: 'right', cellWidth: 32 },
      3: { halign: 'right', cellWidth: 36 },
    },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_activity_count_time_by_type', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportBandwidthUserCountTimePdf(
  rows: BandwidthUserCountTimePoint[],
  documentTitle: string,
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport {
  const head = [['User', 'Activity count', 'Time (hrs)', 'Avg hrs / activity']];
  const body: (string | number)[][] = [];
  let sumCount = 0;
  let sumHrs = 0;
  for (const r of rows) {
    const avg = r.totalActivityCount > 0 ? r.timeSpentHours / r.totalActivityCount : null;
    body.push([
      r.userName,
      r.totalActivityCount,
      bwExportDecimal(r.timeSpentHours),
      bwExportDecimal(avg),
    ]);
    sumCount += r.totalActivityCount;
    sumHrs += r.timeSpentHours;
  }
  const totalAvg = sumCount > 0 ? roundBwDecimal(sumHrs / sumCount) : '';
  const lastIdx = body.length;
  body.push(['Total', sumCount, bwExportDecimal(sumHrs), totalAvg]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, documentTitle, dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 62 },
      1: { halign: 'right', cellWidth: 32 },
      2: { halign: 'right', cellWidth: 32 },
      3: { halign: 'right', cellWidth: 36 },
    },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_activity_count_time_by_user', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

function allocationPdfRow(metrics: BandwidthAllocationMetrics): (string | number)[] {
  return [
    bwExportDecimal(metrics.totalHours),
    bwExportDecimal(metrics.billableHours),
    bwExportDecimal(metrics.nonBillableHours),
    bwExportDecimal(metrics.availableHours),
    allocationExportPctPdf(metrics.bandwidthUtilizedPct),
    allocationExportPctPdf(metrics.bandwidthAvailablePct),
  ];
}

export function exportBandwidthTeamResourceAllocationPdf(
  rows: BandwidthTeamResourceAllocationRow[],
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport | null {
  if (rows.length === 0) return null;
  const head = [[
    'Team / Resource',
    'Type',
    'Total hours',
    'Billable hours',
    'Non billable hours',
    'Available hours',
    'Bandwidth utilized %',
    'Bandwidth available %',
  ]];
  const body: (string | number)[][] = [];
  const boldRows = new Set<number>();
  let rowIdx = 0;

  for (const team of rows) {
    boldRows.add(rowIdx);
    body.push([team.label, 'Team', ...allocationPdfRow(team)]);
    rowIdx++;
    for (const resource of team.resources ?? []) {
      body.push([resource.label, 'Resource', ...allocationPdfRow(resource)]);
      rowIdx++;
    }
  }

  const footer = computeAllocationFooter(
    rows,
    rows.reduce((sum, team) => sum + team.totalHours, 0)
  );
  boldRows.add(rowIdx);
  body.push(['Total', '', ...allocationPdfRow(footer)]);
  const lastBodyIdx = body.length - 1;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, 'Bandwidth Dashboard - Team & Resource Allocation', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 22 },
      2: { halign: 'right', cellWidth: 24 },
      3: { halign: 'right', cellWidth: 24 },
      4: { halign: 'right', cellWidth: 26 },
      5: { halign: 'right', cellWidth: 24 },
      6: { halign: 'right', cellWidth: 28 },
      7: { halign: 'right', cellWidth: 28 },
    },
    didParseCell: (data) => styleBodyCellGrouped(data, boldRows, lastBodyIdx),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_team_resource_allocation', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

function activityAllocationPdfRow(row: BandwidthActivityAllocationRow): (string | number)[] {
  return [
    bwExportDecimal(row.totalHours),
    bwExportDecimal(row.billableHours),
    bwExportDecimal(row.nonBillableHours),
    allocationExportPctPdf(row.billablePct),
    allocationExportPctPdf(row.nonBillablePct),
  ];
}

export function exportBandwidthActivityAllocationPdf(
  rows: BandwidthActivityAllocationRow[],
  dateFrom: string,
  dateTo: string
): BandwidthPdfExport | null {
  if (rows.length === 0) return null;
  const head = [[
    'Activity',
    'Total hours',
    'Billable hours',
    'Non billable hours',
    'Billable %',
    'Non billable %',
  ]];
  const body: (string | number)[][] = [];
  const boldRows = new Set<number>();

  for (const row of rows) {
    body.push([row.activityTypeLabel, ...activityAllocationPdfRow(row)]);
  }

  const footer = computeActivityAllocationFooter(rows);
  const lastBodyIdx = body.length;
  boldRows.add(lastBodyIdx);
  body.push(['Total', ...activityAllocationPdfRow({ activityType: '', activityTypeLabel: '', ...footer })]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawBandwidthPdfTitleBlock(doc, 'Bandwidth Dashboard - Activity Allocation', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: {
      0: { cellWidth: 58 },
      1: { halign: 'right', cellWidth: 26 },
      2: { halign: 'right', cellWidth: 26 },
      3: { halign: 'right', cellWidth: 28 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 22 },
    },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeBandwidthPdfFooters(doc);
  const fileName = bwPdfFilename('bandwidth_activity_allocation', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}
